import type {
  DesignBody,
  FaceKey,
  QuoteDesignViewFont,
  QuoteDesignViewInk,
  ResolvedEditAreaDefinition,
} from '@repo/api-contracts';
import { type ReactElement, useMemo, useState } from 'react';
import { FACE_LABELS } from '../face-labels';
import { useFaceRender } from '../face-render/use-face-render';
import { viewableFaces } from '../viewable-faces';
import { useZoomPan } from '../zoom-pan/use-zoom-pan';
import { FaceNotices } from './face-notices';
import { FaceStage } from './face-stage';
import { FaceTabs } from './face-tabs';
import { ZoomControls } from './zoom-controls';

export interface DesignViewerProps {
  /** 견적에 고정된 편집영역 정의(서명 주소판) — 고정 색상 하나만 들어 있다. */
  readonly definition: ResolvedEditAreaDefinition;
  readonly colorOptionId: string;
  /** 견적 사본의 디자인 본문(견적 상세 design.designBody). */
  readonly body: DesignBody;
  /** 고정 이미지 id → 서명 주소. */
  readonly assetUrls: ReadonlyMap<string, string>;
  readonly fonts: readonly QuoteDesignViewFont[];
  readonly inks: readonly QuoteDesignViewInk[];
  /** 서명 주소가 만료(403)됐을 때 뷰 데이터를 다시 받는다. */
  readonly refreshUrls: () => Promise<void>;
  readonly className?: string;
}

/**
 * 세로 줄 — 면 탭·확대 단추 줄은 제 높이(shrink-0), 그림 무대가 남는 높이를 다 쓴다. 바깥 틀이 높이를 주려면 className 으로
 * `min-h-0 flex-1` 처럼 늘려 준다(창 본문이 세로 flex 여야 한다). 높이를 주지 않으면 무대는 최소 높이(256px)다.
 */
const ROOT_CLASS = 'flex min-w-0 flex-col gap-4';

/**
 * 견적 디자인 읽기 전용 뷰어(뷰어 설계 V8·V10·V11·V12) — 면 탭·그림 무대·확대 단추·안내. 편집·선택·저장은 없다.
 * 그리기 입력은 받은 고정 사본(정의·고정 색상·본문·이미지 주소·폰트·잉크)뿐이다. 고른 면만 그리고, 결과는 면마다 useFaceRender 가 캐시한다.
 * 처음에는 디자인이 있는 첫 면을 보여 준다(없으면 앞면). 확대는 CSS 배율로만 하고, 면을 바꾸면 맞춤으로 돌아간다(resetKey).
 * 확대 키(+ = - 0·화살표)는 뷰어 틀이 받아 면 탭·확대 단추에 포커스가 있어도 듣는다 — 그림이 없으면(그리는 중·실패) 단추처럼 받지 않는다.
 * 바깥 틀(창·닫기)은 앱이 맡는다.
 */
export const DesignViewer = ({
  definition,
  colorOptionId,
  body,
  assetUrls,
  fonts,
  inks,
  refreshUrls,
  className,
}: DesignViewerProps): ReactElement => {
  const faces = useMemo(() => viewableFaces(definition, body), [definition, body]);
  const [faceKey, setFaceKey] = useState<FaceKey>(
    () => faces.find((face) => face.hasLayers)?.face ?? 'front',
  );
  const faceRender = useFaceRender({
    enabled: true,
    definition,
    colorOptionId,
    faceKey,
    body,
    assetUrls,
    fonts,
    inks,
    refreshUrls,
  });
  const { raster } = faceRender;
  // 같은 결과면 같은 객체 — 확대 훅이 크기 변화로 오인해 다시 맞추지 않게 한다.
  const content = useMemo(
    () => (raster === null ? null : { width: raster.width, height: raster.height }),
    [raster],
  );
  const zoom = useZoomPan({ content, resetKey: faceKey });
  const kind = faces.find((face) => face.face === faceKey)?.kind ?? 'design';

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: 안쪽 면 탭·확대 단추·무대(모두 포커스를 받는다)에서 올라온 확대 키를 모아 받는 위임 처리기다 — 틀 자체는 조작 대상이 아니다.
    <div
      className={className === undefined ? ROOT_CLASS : `${ROOT_CLASS} ${className}`}
      onKeyDown={raster === null ? undefined : zoom.onKeyDown}
    >
      <div className="shrink-0 border-b border-foreground/10">
        <FaceTabs faces={faces} value={faceKey} onChange={setFaceKey} />
      </div>
      <FaceStage label={FACE_LABELS[faceKey]} faceRender={faceRender} zoom={zoom} />
      <div className="flex shrink-0 flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
        <FaceNotices
          kind={kind}
          notices={faceRender.status === 'ready' ? faceRender.notices : []}
        />
        <ZoomControls
          percent={raster === null ? null : zoom.percent}
          isFit={zoom.state.level === 'fit'}
          canZoomIn={zoom.canZoomIn}
          canZoomOut={zoom.canZoomOut}
          disabled={raster === null}
          onZoomIn={zoom.zoomIn}
          onZoomOut={zoom.zoomOut}
          onFit={zoom.fit}
        />
      </div>
    </div>
  );
};
